/**
 * 处理Markdown中的格式化元素的完整性（粗体、斜体等）
 */
import { LogDebugFunction } from '../utils/markdownTypes';
import { logMatchDetails } from '../utils/logUtils';

/**
 * 处理粗体格式
 */
export const processBold = (
  text: string, 
  startIdx: number, 
  charsToShow: number, 
  analyzedText: string,
  remainingText: string,
  logDebugInfo: LogDebugFunction
): number => {
  // 粗体处理（优先处理粗体，因为粗体包含两个星号可能会被误判为斜体）
  // 处理普通的粗体 "**加粗**"
  if (/\*\*[^*]*$/.test(analyzedText)) {
    // 不要处理已经匹配过的复合语法 
    if (!/- +\*\*[^*]*$/.test(analyzedText) && 
        !/\d+\. +\*\*[^*]*$/.test(analyzedText) && 
        !/[ \t]+ *- +\*\*[^*]*$/.test(analyzedText) &&
        !/[ \t]+ *\d+\. +\*\*[^*]*$/.test(analyzedText)) {
      // 先尝试匹配闭合的粗体 "**文本**"
      const boldClosed = remainingText.match(/^\*\*([^*\n]+?)\*\*/);
      if (boldClosed) {
        const fullBold = boldClosed[0].length;
        logMatchDetails('粗体-完整', boldClosed, {
          总长度: fullBold
        });
        
        if (charsToShow < fullBold) {
          return logDebugInfo('粗体-完整', fullBold - charsToShow);
        }
      } else {
        // 如果没有闭合，至少显示 ** 加一个字符
        const boldOpened = remainingText.match(/^\*\*([^*\n]*)/);
        if (boldOpened) {
          const minRequired = Math.min(boldOpened[0].length, 3); // ** + 至少一个字符
          if (charsToShow < minRequired) {
            return logDebugInfo('粗体-前缀', minRequired - charsToShow);
          }
        }
      }
    }
  }
  
  return 0;
};

/**
 * 处理斜体格式
 */
export const processItalic = (
  text: string, 
  startIdx: number, 
  charsToShow: number, 
  analyzedText: string,
  remainingText: string,
  logDebugInfo: LogDebugFunction
): number => {
  // 斜体处理（必须确保不是粗体的一部分）
  // 确保分析的文本以单个*结尾，且前面不是*（避免与粗体冲突）
  if (/[^*]\*$/.test(analyzedText) || /^\*$/.test(analyzedText)) {
    // 不要处理已经匹配过的复合语法
    if (!/- +\*[^*]*$/.test(analyzedText) && 
        !/\d+\. +\*[^*]*$/.test(analyzedText) &&
        !/[ \t]+ *- +\*[^*]*$/.test(analyzedText) &&
        !/[ \t]+ *\d+\. +\*[^*]*$/.test(analyzedText)) {
      // 匹配完整的斜体
      const italicClosed = remainingText.match(/^\*([^*\n]+?)\*/);
      if (italicClosed) {
        const fullItalic = italicClosed[0].length;
        logMatchDetails('斜体-完整', italicClosed, {
          总长度: fullItalic
        });
        
        if (charsToShow < fullItalic) {
          return logDebugInfo('斜体-完整', fullItalic - charsToShow);
        }
      } else {
        // 如果没有闭合，至少显示 * 加一个字符
        const italicOpened = remainingText.match(/^\*([^*\n]*)/);
        if (italicOpened) {
          const minRequired = Math.min(italicOpened[0].length, 2); // * + 至少一个字符
          if (charsToShow < minRequired) {
            return logDebugInfo('斜体-前缀', minRequired - charsToShow);
          }
        }
      }
    }
  }
  
  return 0;
};

/**
 * 处理特殊格式化情况
 * 用于处理一些不寻常的Markdown格式组合或不完整的格式标记
 */
export const processSpecialFormatting = (
  text: string, 
  startIdx: number, 
  charsToShow: number, 
  analyzedText: string,
  remainingText: string,
  logDebugInfo: LogDebugFunction
): number => {
  // 处理行首的 * 和 ** （通常是无序列表的一部分）
  if (startIdx === 0 || text.charAt(startIdx - 1) === '\n') {
    // 行首的加粗格式 "**文本**"
    if (remainingText.startsWith('**')) {
      const boldMatch = remainingText.match(/^\*\*([^*\n]+?)\*\*/);
      if (boldMatch) {
        const fullBold = boldMatch[0].length;
        logMatchDetails('行首粗体', boldMatch, {
          总长度: fullBold
        });
        
        if (charsToShow < fullBold) {
          return logDebugInfo('行首粗体-完整', fullBold - charsToShow);
        }
      }
    }
    
    // 行首的斜体格式 "*文本*"
    if (remainingText.startsWith('*') && !remainingText.startsWith('**')) {
      const italicMatch = remainingText.match(/^\*([^*\n]+?)\*/);
      if (italicMatch) {
        const fullItalic = italicMatch[0].length;
        logMatchDetails('行首斜体', italicMatch, {
          总长度: fullItalic
        });
        
        if (charsToShow < fullItalic) {
          return logDebugInfo('行首斜体-完整', fullItalic - charsToShow);
        }
      }
    }
  }
  
  // 处理不完整的粗体格式（如对话框的输出只有"*标题**"的情况）
  if (analyzedText.endsWith('*') && remainingText.startsWith('*')) {
    const match = analyzedText.match(/([^*])\*$/);
    if (match) {
      // 检查这是否是加粗的一部分
      const boldMatch = remainingText.match(/^\*([^*\n]+?)\*\*/);
      if (boldMatch) {
        const fullBold = boldMatch[0].length;
        logMatchDetails('不完整粗体', boldMatch, {
          前置字符: match[1],
          总长度: fullBold
        });
        
        if (charsToShow < fullBold) {
          return logDebugInfo('不完整粗体-修复', fullBold - charsToShow);
        }
      }
    }
  }
  
  return 0;
}; 